import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';
interface MoneySavedCounterProps {
  chartId?: string;
  title?: string;
}
export function MoneySavedCounter({
  chartId = 'main',
  title = 'Diferencia Promedio'
}: MoneySavedCounterProps) {
  const [diffData, setDiffData] = useState({
    avgDiffWithCoordinator: 0,
    avgDiffWithReal: 0,
    avgOurPrediction: 0,
    avgCoordinatorPrediction: 0,
    avgOurError: 0,
    avgCoordError: 0
  });
  const [isImprovement, setIsImprovement] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    const handleDiffDataUpdate = event => {
      const newData = event.detail;
      if (newData.avgOurPrediction !== 0 || newData.avgCoordinatorPrediction !== 0) {
        setDiffData(newData);
        setIsImprovement(newData.avgDiffWithReal > 0);
        setIsLoading(false);
      }
    };
    const existingData = window[`forecastDiffData_${chartId}`];
    if (existingData && (existingData.avgOurPrediction !== 0 || existingData.avgCoordinatorPrediction !== 0)) {
      setDiffData(existingData);
      setIsImprovement(existingData.avgDiffWithReal > 0);
      setIsLoading(false);
    }
    window.addEventListener(`forecastDiffDataUpdated_${chartId}`, handleDiffDataUpdate);
    return () => {
      window.removeEventListener(`forecastDiffDataUpdated_${chartId}`, handleDiffDataUpdate);
    };
  }, [chartId]);
  const errorDifference = Math.abs(diffData.avgDiffWithReal);
  const percentageDiff = diffData.avgCoordError !== 0 ? errorDifference / diffData.avgCoordError * 100 : 0;
  if (isLoading) {
    return <div className="rounded-2xl p-6 min-w-[280px] shadow-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-id="element-160">
        <div className="text-xs font-medium mb-4 text-slate-500 dark:text-slate-400 uppercase tracking-wide" data-id="element-161">
          {title}
        </div>
        <div className="space-y-3" data-id="element-162">
          <div className="flex items-baseline gap-3" data-id="element-163">
            <div className="h-10 w-28 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded-lg animate-pulse" data-id="element-164" />
            <div className="h-6 w-20 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded animate-pulse" data-id="element-165" />
          </div>
          <div className="h-8 w-48 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded-full animate-pulse" data-id="element-166" />
          <div className="h-3 w-40 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded animate-pulse" data-id="element-167" />
        </div>
      </div>;
  }
  return <div className="rounded-2xl p-6 min-w-[280px] shadow-lg border border-slate-200 dark:border-slate-800 hover:shadow-xl dark:hover:shadow-black/30 transition-shadow duration-300 bg-white dark:bg-slate-900" data-id="element-168">
      <div className="flex items-center justify-between mb-4" data-id="element-169">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide" data-id="element-170">
          {title}
        </div>
        <div className="relative" data-id="element-171">
          <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" aria-label="Información sobre diferencia promedio" data-id="element-172">
            <Info size={16} data-id="element-173" />
          </button>

          {showTooltip && <div className="absolute right-0 top-6 z-50 w-72 p-4 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 backdrop-blur-sm" data-id="element-174">
              <div className="text-xs font-semibold mb-2 text-slate-900 dark:text-slate-50" data-id="element-175">
                ¿Qué es la Diferencia Promedio?
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed space-y-2" data-id="element-176">
                <p data-id="element-177">
                  Mide cuánto mejor (o peor) es nuestra proyección comparada con
                  el Coordinador Eléctrico Nacional.
                </p>
                <p data-id="element-178">
                  El valor muestra la diferencia promedio en USD/MWh entre los
                  errores de predicción. Un valor positivo significa que nuestra
                  proyección es más precisa.
                </p>
                <p className="text-slate-500 dark:text-slate-500 italic" data-id="element-179">
                  Calculado comparando ambas proyecciones contra los datos
                  reales del sistema.
                </p>
              </div>
            </div>}
        </div>
      </div>

      <div className="flex items-baseline gap-3 mb-4" data-id="element-180">
        <div className={`flex items-center gap-2 ${isImprovement ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} data-id="element-181">
          {isImprovement ? <TrendingUp size={24} className="drop-shadow-sm" data-id="element-182" /> : <TrendingDown size={24} className="drop-shadow-sm" data-id="element-183" />}
          <span className="text-4xl font-bold" data-id="element-184">
            {errorDifference.toFixed(2)}
          </span>
        </div>
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400" data-id="element-185">
          USD/MWh
        </span>
      </div>

      <div className="space-y-2" data-id="element-186">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${isImprovement ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-500/20' : 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-500/20'}`} data-id="element-187">
          {isImprovement && errorDifference !== 0 ? `${percentageDiff.toFixed(1)}% mejor que coordinador` : errorDifference === 0 ? 'Igual al coordinador' : `${percentageDiff.toFixed(1)}% peor que coordinador`}
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-188">
          Basado en datos históricos
        </div>
      </div>
    </div>;
}